import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Star, Heart, ShoppingCart, Truck, Shield, RefreshCcw, Minus, Plus, ChevronLeft, Share2, Flame, Clock, MessageSquare, Zap, Trophy } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
import { useCurrency } from '../components/CurrencySelector';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { ProductCard } from '../components/ProductCard';
import { ReviewForm, ReviewList, ReviewSummary } from '../components/ReviewComponents';
import { ImageZoom } from '../components/ImageZoom';
import { VariantSelector } from '../components/VariantSelector';
import { QuoteRequestButton } from '../components/QuoteRequestButton';
import { FlashSaleCountdown, isFlashActive, isFlashScheduled } from '../components/FlashSaleCountdown';
import { RestockAlertForm } from '../components/RestockAlertForm';
import { PriceWatchButton } from '../components/PriceWatchButton';
import { PriceHistoryChart } from '../components/PriceHistoryChart';
import { ProductQnA } from '../components/ProductQnA';
import { recordProductView } from '../components/RecommendedProducts';
import { usePageT } from '../i18n/usePageT';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language, getLocalizedName, getLocalizedDescription } = useLanguage();
  const T = usePageT();
  const { format } = useCurrency();
  const { isAuthenticated, token, user } = useAuth();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [ratingDistribution, setRatingDistribution] = useState({ 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });
  const [contactingVendor, setContactingVendor] = useState(false);
  const [flashExpired, setFlashExpired] = useState(false);
  const [, forceTick] = useState(0);
  const [myVendorId, setMyVendorId] = useState(null);
  const [answeredQna, setAnsweredQna] = useState([]);

  useEffect(() => {
    if (!product?.id) return;
    recordProductView(product.id);
    axios.get(`${API}/products/${product.id}/questions`)
      .then((r) => setAnsweredQna((r.data || []).filter((q) => q.answer?.content)))
      .catch(() => {});
  }, [product?.id]);

  useEffect(() => {
    if (user?.role === 'vendor' && token) {
      axios.get(`${API}/vendors/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setMyVendorId(r.data.id))
        .catch(() => {});
    }
  }, [user?.role, token]);

  const contactVendor = async () => {
    if (!isAuthenticated) {
      toast.info(language === 'fr' ? 'Connectez-vous pour contacter le vendeur' : 'Sign in to contact the seller');
      navigate('/login');
      return;
    }
    setContactingVendor(true);
    try {
      const r = await axios.post(
        `${API}/conversations`,
        {
          vendor_id: product.vendor_id,
          product_id: product.id,
          initial_message: language === 'fr'
            ? `Bonjour, j'aimerais des informations sur le produit "${product.name_fr || product.name}".`
            : `Hi, I'd like more info on "${product.name_en || product.name}".`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/messages/${r.data.conversation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setContactingVendor(false);
    }
  };

  const handleVariantChange = (type, value) => {
    setSelectedVariants((s) => ({ ...s, [type]: value }));
  };

  const fetchReviews = useCallback(async () => {
    try {
      const reviewsRes = await axios.get(`${API}/reviews/product/${id}`);
      setReviews(reviewsRes.data);
      
      // Calculate rating distribution
      const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      reviewsRes.data.forEach(r => {
        if (dist[r.rating] !== undefined) dist[r.rating]++;
      });
      setRatingDistribution(dist);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [id]);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const productRes = await axios.get(`${API}/products/${id}`);
        setProduct(productRes.data);
        
        await fetchReviews();

        // Fetch related products
        if (productRes.data.category_id) {
          const relatedRes = await axios.get(`${API}/products?category_id=${productRes.data.category_id}&limit=4`);
          setRelatedProducts(relatedRes.data.filter(p => p.id !== id));
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error(language === 'fr' ? 'Produit non trouvé' : 'Product not found');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate, language, fetchReviews]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error(language === 'fr' ? 'Connectez-vous pour ajouter au panier' : 'Login to add to cart');
      navigate('/login');
      return;
    }

    try {
      await addToCart(product.id, quantity);
      toast.success(language === 'fr' ? 'Ajouté au panier' : 'Added to cart');
    } catch (error) {
      toast.error(error.message || 'Error');
    }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      toast.error(language === 'fr' ? 'Connectez-vous' : 'Please login');
      return;
    }

    try {
      if (isWishlisted) {
        await axios.delete(`${API}/wishlist/${product.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsWishlisted(false);
        toast.success(language === 'fr' ? 'Retiré des favoris' : 'Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/${product.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsWishlisted(true);
        toast.success(language === 'fr' ? 'Ajouté aux favoris' : 'Added to wishlist');
      }
    } catch (error) {
      console.error('Wishlist error:', error);
    }
  };

  const flashActive = !flashExpired && isFlashActive(product);
  const flashScheduled = !flashActive && isFlashScheduled(product);
  const canReply = !!product && (user?.role === 'admin' || (myVendorId && product.vendor_id === myVendorId));
  const discount = flashActive
    ? Math.round((1 - product.flash_sale_price / product.price) * 100)
    : product?.compare_price
      ? Math.round((1 - product.price / product.compare_price) * 100)
      : 0;

  if (loading) {
    return (
      <div className="container-main py-8">
        <div className="grid md:grid-cols-2 gap-12">
          <Skeleton className="aspect-square rounded-2xl" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const productUrl = `${window.location.origin}/product/${product.id}`;
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: getLocalizedName(product),
      description: (getLocalizedDescription(product) || '').replace(/\s+/g, ' ').trim().slice(0, 500),
      image: product.images || [],
      sku: product.id,
      brand: { '@type': 'Brand', name: product.vendor_name },
      ...(product.review_count > 0
        ? { aggregateRating: { '@type': 'AggregateRating', ratingValue: Number((product.rating || 0).toFixed(1)), reviewCount: product.review_count } }
        : {}),
      ...(reviews.length > 0
        ? {
            review: reviews.slice(0, 10).map((r) => ({
              '@type': 'Review',
              author: { '@type': 'Person', name: r.user_name || 'Client' },
              datePublished: (r.created_at || '').slice(0, 10),
              name: r.title || undefined,
              reviewBody: r.content || '',
              reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
            })),
          }
        : {}),
      offers: {
        '@type': 'Offer',
        url: productUrl,
        priceCurrency: 'EUR',
        price: Number(((flashActive ? product.flash_sale_price : product.price) || 0).toFixed(2)),
        ...(flashActive ? { priceValidUntil: String(product.flash_sale_end).slice(0, 10) } : {}),
        availability: product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@type': 'Organization', name: product.vendor_name },
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: language === 'fr' ? 'Accueil' : 'Home', item: window.location.origin },
        { '@type': 'ListItem', position: 2, name: language === 'fr' ? 'Boutique' : 'Shop', item: `${window.location.origin}/shop` },
        { '@type': 'ListItem', position: 3, name: getLocalizedName(product), item: productUrl },
      ],
    },
    ...(answeredQna.length > 0
      ? [{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: answeredQna.slice(0, 20).map((q) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: { '@type': 'Answer', text: q.answer.content },
          })),
        }]
      : []),
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      <SEO
        title={getLocalizedName(product)}
        description={getLocalizedDescription(product)}
        image={product.images?.[0]}
        url={`${window.location.origin}/product/${product.id}`}
        type="product"
        jsonLd={jsonLd}
      />
      <div className="container-main py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t('back')}
        </Button>

        {/* Product Main */}
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          {/* Images */}
          <div className="space-y-4">
            <div className="relative">
              <ImageZoom
                src={product.images?.[selectedImage] || 'https://via.placeholder.com/600'}
                alt={getLocalizedName(product)}
              />
              {discount > 0 && (
                <Badge
                  className={`absolute top-4 left-4 text-lg px-4 py-1 z-10 ${flashActive ? 'bg-red-600 text-white hover:bg-red-600' : 'bg-[#E9C46A] text-[#0F4C5C]'}`}
                  data-testid={flashActive ? 'flash-sale-badge-detail' : 'discount-badge-detail'}
                >
                  {flashActive && <Zap className="w-4 h-4 mr-1 fill-current inline" />}
                  -{discount}%
                </Badge>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-[#0F4C5C]' : 'border-transparent'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            {/* Vendor */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('soldBy')}</span>
                <Badge variant="secondary" className="font-medium">
                  {product.vendor_name}
                </Badge>
              </div>
              <button
                type="button"
                onClick={contactVendor}
                disabled={contactingVendor}
                className="text-sm font-semibold text-[#0F4C5C] hover:text-[#E9C46A] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                data-testid="contact-vendor-btn"
              >
                <MessageSquare className="w-4 h-4" />
                {language === 'fr' ? 'Contacter le vendeur' : 'Contact the seller'}
              </button>
            </div>

            {/* B2B quote */}
            <div>
              <QuoteRequestButton product={product} />
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold" data-testid="product-title">
              {getLocalizedName(product)}
            </h1>
            {product.is_bestseller && (
              <Badge className="bg-[#0F4C5C] text-[#E9C46A] font-bold hover:bg-[#0F4C5C] w-fit" data-testid="bestseller-badge-detail">
                <Trophy className="w-3.5 h-3.5 mr-1.5" />
                {T('Meilleure vente', 'Best seller')}
              </Badge>
            )}

            {/* Rating */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.floor(product.rating || 0)
                        ? 'fill-[#E9C46A] text-[#E9C46A]'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="font-medium">{product.rating?.toFixed(1) || '0.0'}</span>
              <span className="text-muted-foreground">
                ({product.review_count || 0} {t('reviews').toLowerCase()})
              </span>
            </div>

            {/* Price */}
            <div className="space-y-3">
              <div className="flex items-baseline gap-4">
                <span className={`text-4xl font-bold ${flashActive ? 'text-red-600' : 'text-[#0F4C5C]'}`} data-testid="product-price">
                  {format(flashActive ? product.flash_sale_price : product.price)}
                </span>
                {flashActive ? (
                  <span className="text-xl text-muted-foreground line-through">
                    {format(product.price)}
                  </span>
                ) : product.compare_price && (
                  <span className="text-xl text-muted-foreground line-through">
                    {format(product.compare_price)}
                  </span>
                )}
              </div>
              {flashActive && (
                <FlashSaleCountdown
                  endDate={product.flash_sale_end}
                  size="lg"
                  onExpire={() => setFlashExpired(true)}
                  testId="flash-countdown-detail"
                />
              )}
              {flashScheduled && (
                <div className="space-y-2">
                  <FlashSaleCountdown
                    endDate={product.flash_sale_start}
                    size="lg"
                    upcoming
                    onExpire={() => forceTick((n) => n + 1)}
                    testId="flash-upcoming-detail"
                  />
                  <p className="text-sm font-semibold text-amber-700" data-testid="flash-upcoming-price">
                    {T('Prix flash à venir : {price}', 'Upcoming flash price: {price}').replace('{price}', format(product.flash_sale_price))}
                  </p>
                </div>
              )}
              <div>
                <PriceWatchButton productId={product.id} />
              </div>
              <PriceHistoryChart productId={product.id} />
            </div>

            {/* Description */}
            <p className="text-muted-foreground leading-relaxed">
              {getLocalizedDescription(product)}
            </p>

            {/* Stock & Urgency */}
            <div className="flex flex-wrap items-center gap-2">
              {product.stock > 0 ? (
                <>
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                    <span className="w-2 h-2 rounded-full bg-green-500 mr-1.5"></span>
                    {t('inStock')}
                  </Badge>
                  {product.stock <= 5 && (
                    <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100" data-testid="stock-urgency">
                      <Flame className="w-3 h-3 mr-1" />
                      {language === 'fr'
                        ? `Plus que ${product.stock} en stock !`
                        : `Only ${product.stock} left!`}
                    </Badge>
                  )}
                  {product.review_count > 10 && (
                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                      <Clock className="w-3 h-3 mr-1" />
                      {language === 'fr' ? 'Très demandé' : 'Selling fast'}
                    </Badge>
                  )}
                </>
              ) : (
                <Badge variant="destructive">{t('outOfStock')}</Badge>
              )}
            </div>

            {/* Back-in-stock alert */}
            {product.stock === 0 && <RestockAlertForm productId={product.id} />}

            {/* Variant Selector */}
            <VariantSelector
              variants={product.variants || []}
              selected={selectedVariants}
              onChange={handleVariantChange}
            />

            {/* Quantity Selector */}
            <div className="flex items-center gap-4">
              <span className="font-medium">{t('quantity')}:</span>
              <div className="flex items-center border border-border rounded-lg">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  disabled={quantity >= product.stock}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <Button
                className="flex-1 btn-primary h-14 text-lg"
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {t('addToCart')}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className={`h-14 w-14 ${isWishlisted ? 'text-red-500 border-red-200' : ''}`}
                onClick={handleWishlistToggle}
                data-testid="wishlist-btn"
              >
                <Heart className={`w-6 h-6 ${isWishlisted ? 'fill-current' : ''}`} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-14 w-14"
              >
                <Share2 className="w-5 h-5" />
              </Button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
              <div className="text-center">
                <Truck className="w-6 h-6 mx-auto mb-2 text-[#2A9D8F]" />
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Livraison rapide' : 'Fast delivery'}
                </p>
              </div>
              <div className="text-center">
                <Shield className="w-6 h-6 mx-auto mb-2 text-[#2A9D8F]" />
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Paiement sécurisé' : 'Secure payment'}
                </p>
              </div>
              <div className="text-center">
                <RefreshCcw className="w-6 h-6 mx-auto mb-2 text-[#2A9D8F]" />
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Retour 30 jours' : '30-day returns'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="description" className="mb-16">
          <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent">
            <TabsTrigger 
              value="description" 
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F4C5C] data-[state=active]:bg-transparent"
            >
              {t('description')}
            </TabsTrigger>
            <TabsTrigger 
              value="specifications"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F4C5C] data-[state=active]:bg-transparent"
            >
              {t('specifications')}
            </TabsTrigger>
            <TabsTrigger 
              value="reviews"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F4C5C] data-[state=active]:bg-transparent"
            >
              {t('reviews')} ({reviews.length})
            </TabsTrigger>
            <TabsTrigger 
              value="qna"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0F4C5C] data-[state=active]:bg-transparent"
              data-testid="qna-tab-trigger"
            >
              {T('Questions & Réponses', 'Questions & Answers')}
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="description" className="pt-6">
            <p className="text-muted-foreground leading-relaxed max-w-3xl">
              {getLocalizedDescription(product)}
            </p>
          </TabsContent>
          
          <TabsContent value="specifications" className="pt-6">
            {product.attributes && Object.keys(product.attributes).length > 0 ? (
              <div className="max-w-xl">
                {Object.entries(product.attributes).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-3 border-b border-border last:border-0">
                    <span className="font-medium capitalize">{key}</span>
                    <span className="text-muted-foreground">{value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">
                {language === 'fr' ? 'Aucune spécification disponible' : 'No specifications available'}
              </p>
            )}
          </TabsContent>
          
          <TabsContent value="reviews" className="pt-6">
            <div className="max-w-3xl">
              {/* Review Summary */}
              <ReviewSummary 
                rating={product?.rating} 
                reviewCount={product?.review_count || reviews.length}
                ratingDistribution={ratingDistribution}
              />
              
              {/* Write Review Button */}
              {isAuthenticated && (
                <div className="mb-8">
                  <ReviewForm 
                    productId={id}
                    productName={getLocalizedName(product)}
                    onReviewSubmitted={async () => {
                      await fetchReviews();
                      // Refresh product to get updated rating
                      const res = await axios.get(`${API}/products/${id}`);
                      setProduct(res.data);
                    }}
                  />
                </div>
              )}
              
              {/* Reviews List */}
              <ReviewList reviews={reviews} loading={loading} canReply={canReply} onReplied={fetchReviews} />
            </div>
          </TabsContent>

          <TabsContent value="qna" className="pt-6">
            <ProductQnA productId={id} />
          </TabsContent>
        </Tabs>

        {/* You may also like */}
        {relatedProducts.length > 0 && (
          <section className="border-t border-[#0F4C5C]/10 pt-12">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#E9C46A] font-semibold">
                  {language === 'fr' ? 'Découvertes' : 'Discoveries'}
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-[#0F4C5C] mt-1 tracking-tight">
                  {language === 'fr' ? 'Vous aimerez aussi' : 'You may also like'}
                </h2>
              </div>
              <button
                onClick={() => navigate(`/shop?category=${product.category_id}`)}
                className="hidden md:flex items-center gap-1.5 text-sm font-semibold text-[#0F4C5C] hover:text-[#E9C46A] transition-colors"
                data-testid="view-more-similar"
              >
                {language === 'fr' ? 'Voir tout' : 'View all'}
                <ChevronLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
            <div className="product-grid">
              {relatedProducts.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
